import { resolveTheme } from 'theme/resolveTheme';
import { DEFAULT_STORE } from './defaultStore';

const str = (v, fallback = null) => (typeof v === 'string' && v.trim() ? v.trim() : fallback);

/**
 * `checkout` block of the profile: the terms link, whether an e-mail is
 * required and whether store pickup is offered. An older server sends none:
 * no terms link (the default terms page is used), e-mail optional, pickup
 * decided by the shipping options themselves.
 */
function mergeCheckout(raw) {
  const c = raw && typeof raw === 'object' ? raw : {};
  return {
    termsUrl: str(c.termsUrl),
    requireEmail: c.requireEmail === true,
    pickupEnabled: c.pickupEnabled !== false,
    // How long a payment stays valid (the shop's setting), when the profile says; null otherwise.
    paymentExpiryMinutes: Number.isInteger(c.paymentExpiryMinutes) && c.paymentExpiryMinutes > 0 ? c.paymentExpiryMinutes : null,
  };
}

/**
 * `payment` block of the profile: the payment groups the shop accepts, as
 * display data `{ channel, label }` (qris, virtual_account, ewallet, card,
 * retail, paylater). Read-only for the shopper; the page words them itself
 * (utils/paymentCopy.js). An older server sends none: no groups, neutral copy.
 */
export function mergePayment(raw, bankRaw = null) {
  const p = raw && typeof raw === 'object' ? raw : {};
  const list = Array.isArray(p.groups) ? p.groups : [];
  const seen = new Set();
  const groups = list
    .map((g) => (typeof g === 'string' ? { channel: g, label: null } : g))
    .filter((g) => g && typeof g === 'object' && str(g.channel))
    .map((g) => ({ channel: str(g.channel), label: str(g.label) }))
    .filter((g) => (seen.has(g.channel) ? false : seen.add(g.channel)));
  return { groups, bankAccounts: mergeBankAccounts(p.bankAccounts || bankRaw) };
}

/**
 * The shop's bank accounts for transfers (`{ bank, accountNumber, accountName }`), when the public profile states
 * them: shown to a buyer of a staff order the shop collects by transfer (docs/MIDTRANS.md 13.1). None: the shop sends
 * the details itself.
 */
export function mergeBankAccounts(raw) {
  return (Array.isArray(raw) ? raw : [])
    .map((b) => (b && typeof b === 'object' ? { bank: str(b.bank), accountNumber: str(b.accountNumber), accountName: str(b.accountName) } : null))
    .filter((b) => b && b.accountNumber);
}

/**
 * Merge an API store profile over the defaults, tolerating missing or
 * partial data (the server may be older/newer than this build).
 */
export function mergeStoreProfile(value) {
  const d = DEFAULT_STORE;
  if (!value || typeof value !== 'object') return d;
  const id = value.identity || {};
  const addr = id.address || {};

  return {
    identity: {
      name: str(id.name, d.identity.name),
      tagline: str(id.tagline),
      logoUrl: str(id.logoUrl),
      faviconUrl: str(id.faviconUrl),
      whatsapp: str(id.whatsapp),
      email: str(id.email),
      phone: str(id.phone),
      address: {
        line1: str(addr.line1),
        city: str(addr.city),
        province: str(addr.province),
        postalCode: str(addr.postalCode),
      },
    },
    // Invalid or missing tokens are filled from the theme's preset.
    theme: resolveTheme(value.theme),
    locale: str(value.locale, d.locale),
    currency: str(value.currency, d.currency),
    timezone: str(value.timezone, d.timezone),
    // Slugs of the published info pages; null when the server does not say.
    pages: Array.isArray(value.pages)
      ? value.pages.map((p) => str(p && p.slug)).filter(Boolean)
      : d.pages,
    checkout: mergeCheckout(value.checkout),
    payment: mergePayment(value.payment, value.bankAccounts),
    // Phase 3 (area search, instant delivery, map tiles): read by checkout/shipping.js shippingProfile().
    shipping: value.shipping && typeof value.shipping === 'object' ? value.shipping : null,
    // Articles (docs/PHASE4.md P1): linked from the header and footer only
    // when the shop turned them on and has at least one published.
    articles: mergeArticles(value.articles),
    // The shop's own wording; null = use the built-in text (older server).
    copy: {
      relatedProductsTitle: str(value.copy && value.copy.relatedProductsTitle),
    },
  };
}

/**
 * `articles` block of the profile: `{ enabled }`, true when the shop turned
 * articles on (seo.site.articlesEnabled) and has published at least one.
 * A `count` of 0, if a server sends one, hides the link too. An older server
 * sends none: no article links.
 */
export function mergeArticles(raw) {
  const a = raw && typeof raw === 'object' ? raw : {};
  const enabled = a.enabled === true;
  const empty = Number.isInteger(a.count) && a.count <= 0;
  return { enabled, show: enabled && !empty };
}

/** One-line address for display, skipping empty parts. */
export function formatAddress(address) {
  if (!address) return '';
  const cityLine = [address.city, address.province, address.postalCode].filter(Boolean).join(', ');
  return [address.line1, cityLine].filter(Boolean).join(', ');
}

// v2: theme contract v2. Older cached profiles are simply ignored.
const CACHE_KEY = 'mxc.store.v2';

/** Last good profile, so repeat visits paint with the right branding at once. */
export function readCachedStore() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? mergeStoreProfile(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function writeCachedStore(value) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable: fine */
  }
}
